import { autoinject, transient } from 'aurelia-dependency-injection';
import { computedFrom } from 'aurelia-binding';
import { DialogClientService } from '../../services/basic/dialog-client.service';
import { NotificationClientService, NotificationType } from '../../services/basic/notification-client.service';
import { ConfigService } from '../../services/basic/config.service';
import { PaymentTypeEnum, type RightStoreCheckoutModel, type RightStoreCheckoutProductInformationModel, type RightStoreCheckoutPurchaseModel } from '../../models/rightstore/rightstore.models';
import { RightStoreService } from '../../services/rightstore/rightstore.service';

require("./dialog-order-summary.scss");

@autoinject
@transient()
export class DialogOrderSummary {

    private model: RightStoreCheckoutModel;
    private product: RightStoreCheckoutProductInformationModel;
    private purchaseModel: RightStoreCheckoutPurchaseModel;
    public isLoading: boolean;

    public isProcessingPayment: boolean;
    public purchaseCompleted: boolean;
    public validPurchaseMessage: string;

    public useRightstoreCredits: boolean;
    public useCreditCards: boolean;
    public useInvoice: boolean;

    public canCustomerInvoice: boolean;
    public priceOverride: number;

    public isStandard: boolean = true;
    public isPriority: boolean;

    public creditCards: { name: string }[] = [
        { name: 'visa' },
        { name: 'mastercard' },
        { name: 'amex' },
        { name: 'jcb' },
    ];

    constructor(private dialogClientService: DialogClientService, private rightStoreService: RightStoreService,
        private notificationClientService: NotificationClientService, private configService: ConfigService) {
    }

    activate(model: RightStoreCheckoutModel) {
        this.isLoading = true;
        this.model = model;
    }

    async attached() {
        try {
            this.product = await this.rightStoreService.requestProductInformation(this.model.customerNumber, this.model.productNumber, this.model.userName, this.model.quantity);
            this.priceOverride = this.product.totalPrice;
            this.canCustomerInvoice = this.model.canCustomerInvoice;

            this.purchaseModel = this.getCustomerPurchaseModel(this.model);
            this.setDefaultPaymentMethod();
            this.isLoading = false;
        } catch (e) {
            this.notificationClientService.show('Error occurs when retrieving product information.', '', NotificationType.Error);
            this.close();
        }
    }

    @computedFrom('useRightstoreCredits')
    get shouldDisableBudget() {
        return !this.useRightstoreCredits;
    }

    @computedFrom("product.availablePaymentMethods", "product.availablePaymentMethods.length")
    get shouldDisablePaymentMethod() {
        return this.product?.availablePaymentMethods.length == 0;
    }

    @computedFrom("product.lastPurchasedOn", "product.previousPurchaseStillValid", "product.availablePaymentMethods", "product.totalPrice", "isStandard")
    get shouldDisablePurchase() {
        if (this.isStandard) return false;
        if (this.product?.previousPurchaseStillValid) {
            const options: any = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
            this.validPurchaseMessage = 'You have already purchased this product on ' + new Date(this.product.lastPurchasedOn).
                toLocaleDateString("en-UK", options);
            return true;
        }
        return this.product?.availablePaymentMethods?.length == 0;
    }

    @computedFrom("product.availablePaymentMethods")
    get canUseRightStoreCredits() {
        return this.product?.availablePaymentMethods?.includes(PaymentTypeEnum.RightStoreCredits);
    }

    @computedFrom("product.availablePaymentMethods")
    get canUseInvoice() {
        return this.product?.availablePaymentMethods?.includes(PaymentTypeEnum.Invoice);
    }

    @computedFrom("product.availablePaymentMethods")
    get canUseCreditCards() {
        return this.product?.availablePaymentMethods?.includes(PaymentTypeEnum.CreditCards);
    }

    @computedFrom("product.totalPrice", "product.isQuoteRequired", "isStandard")
    get isFreeProduct() {
        return this.product?.totalPrice == 0 || this.product?.isQuoteRequired || this.isStandard;
    }

    @computedFrom("product.totalPrice", "model.creditBalance")
    get hasSufficientBalance() {
        return this.model && this.model.creditBalance >= this.product.totalPrice
    }

    @computedFrom("priceOverride", "model.creditBalance")
    get hasSufficientBalanceOverride() {
        return this.model && this.model.creditBalance >= this.priceOverride
    }

    close() {
        this.dialogClientService.hide(true);
    }

    async confirm() {
        try {
            let url = "";
            if (this.isStandard) {
                url = await this.model.processStandardProduct();
            } else {
                url = await this.purchaseProduct();
            }

            this.setProcessingPaymentCompleted();
            if (this.model.afterPurchase) {
                const result = this.model.afterPurchase(url, this.isPriority);
                if (result instanceof (Promise)) {
                    await result;
                }
            }
        }
        catch (e) {
            this.notificationClientService.show('Error occurs when purchasing product.', '', NotificationType.Error);
            this.close();
        } finally {
            this.close();
        }
    }

    private purchaseProduct = async () => {
        if (this.useRightstoreCredits && !this.hasSufficientBalanceOverride)
            location.href = this.configService.config.urls.rightStoreTopupsUrl;

        this.isProcessingPayment = true;
        this.purchaseModel.paymentMethod = this.getPaymentMethodEnum();
        if (this.priceOverride != this.product.totalPrice) {
            const priceOverride = `priceOverride=${this.priceOverride}`;
            this.purchaseModel.queryParams = this.purchaseModel.queryParams ? `${this.purchaseModel.queryParams}&${priceOverride}` : priceOverride;
        }
        const val = await this.rightStoreService.purchaseProduct(this.purchaseModel);
        if (!this.model.preventRedirect) {
            location.href = val.fullfillmentUrl;
        }
        return val.fullfillmentUrl;
    }

    public selectStandard(isStandard: boolean) {
        if (isStandard) {
            this.isStandard = true;
            this.isPriority = false;
        }
        else {
            this.isStandard = false
            this.isPriority = true;
        }
    }

    private getCustomerPurchaseModel = (model: RightStoreCheckoutModel): RightStoreCheckoutPurchaseModel => {
        return {
            checkoutQuantity: model.quantity ?? 1,
            customerNumber: model.customerNumber,
            queryParams: model.queryParams,
            userName: model.userName,
            productNumber: this.product.productNumber
        }
    }

    private setDefaultPaymentMethod = () => {
        if (this.canUseInvoice && this.canCustomerInvoice) this.useInvoice = true;
        else if (this.canUseRightStoreCredits) this.useRightstoreCredits = true;
        else if (this.canUseCreditCards) this.useCreditCards = true;
    }

    private setProcessingPaymentCompleted() {
        this.isProcessingPayment = false;
        this.purchaseCompleted = true;
    }

    private getPaymentMethodEnum = (): number => {
        if (this.useRightstoreCredits)
            return PaymentTypeEnum.RightStoreCredits;
        else if (this.useCreditCards)
            return PaymentTypeEnum.CreditCards;
        else if (this.useInvoice)
            return PaymentTypeEnum.Invoice;
        return 0;
    }

    public navigateToRightStoreTopupProducts() {
        window.open(this.configService.config.urls.rightStoreTopupsUrl, '_blank');
    }
}
